// client/src/pages/DataAnalytics/components/TrendAnalysis.js
// Trendanalys-vyn. Multi-line SVG-diagram med index-normaliserade
// referenskurvor för postnummer/stad/land-jämförelse.
import API_CONFIG from '../../../config/apiConfig';
import { ALL_SYMPTOMS } from '../../../components/shared/constants/symptoms';
import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './TrendAnalysis.css';

// Multi-line dark-themed SVG Chart component with INDEX-NORMALIZED reference curves
const SimpleLineChart = ({ 
  data,           // Primary data (postal/city/country depending on scope)
  cityData,       // City reference data (when scope is postal)
  countryData,    // Country reference data (always available)
  height = 400, 
  analysisMode = 'single',
  geographicScope = 'postal'
}) => {
  const { t } = useTranslation();

  if (!data || data.length === 0) {
    return <div className="chart-no-data">{t('trendAnalysis.chart.noData')}</div>;
  }
  
  const chartWidth = Math.max(600, data.length * 80);
  
  // Color scheme for different geographic levels
  const colors = {
    postal: { line: '#64d2ff', glow: 'rgba(100, 210, 255, 0.5)', label: t('trendAnalysis.chart.postalLabel') },
    city: { line: '#bf5af2', glow: 'rgba(191, 90, 242, 0.5)', label: t('trendAnalysis.chart.cityLabel') },
    country: { line: '#ff9f0a', glow: 'rgba(255, 159, 10, 0.4)', label: t('trendAnalysis.chart.countryLabel') }
  };
  
  // Helper: Normalize dataset to index (max = 100)
  const normalizeData = (rawData) => {
    if (!rawData || rawData.length === 0) return { normalized: [], maxValue: 0 };
    const maxValue = Math.max(...rawData.map(d => d.count || 0), 1);
    const normalized = rawData.map(d => ({
      ...d,
      indexValue: maxValue > 0 ? Math.round((d.count || 0) / maxValue * 100) : 0
    }));
    return { normalized, maxValue };
  };
  
  // Determine which datasets to show based on scope, with normalization
  const datasets = [];
  
  if (geographicScope === 'postal') {
    const primaryNorm = normalizeData(data);
    datasets.push({ 
      data: primaryNorm.normalized, 
      maxValue: primaryNorm.maxValue,
      ...colors.postal, 
      isPrimary: true 
    });
    
    if (cityData && cityData.length > 0) {
      const cityNorm = normalizeData(cityData);
      datasets.push({ 
        data: cityNorm.normalized, 
        maxValue: cityNorm.maxValue,
        ...colors.city, 
        isPrimary: false 
      });
    }
    if (countryData && countryData.length > 0) {
      const countryNorm = normalizeData(countryData);
      datasets.push({ 
        data: countryNorm.normalized, 
        maxValue: countryNorm.maxValue,
        ...colors.country, 
        isPrimary: false 
      });
    }
  } else if (geographicScope === 'city') {
    const primaryNorm = normalizeData(data);
    datasets.push({ 
      data: primaryNorm.normalized, 
      maxValue: primaryNorm.maxValue,
      ...colors.city, 
      isPrimary: true 
    });
    
    if (countryData && countryData.length > 0) {
      const countryNorm = normalizeData(countryData);
      datasets.push({ 
        data: countryNorm.normalized, 
        maxValue: countryNorm.maxValue,
        ...colors.country, 
        isPrimary: false 
      });
    }
  } else {
    const primaryNorm = normalizeData(data);
    datasets.push({ 
      data: primaryNorm.normalized, 
      maxValue: primaryNorm.maxValue,
      ...colors.country, 
      isPrimary: true 
    });
  }
  
  // Y-axis is now always 0-100 (index)
  const maxIndex = 100;
  
  return (
    <div className="chart-container">
      <svg width="100%" height={height - 60} viewBox={`0 0 ${chartWidth} ${height - 60}`}>
        {/* Gradient definition for glow effect */}
        <defs>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          <filter id="glowLight">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        {/* Grid lines */}
        {[0, 25, 50, 75, 100].map(percent => (
          <line 
            key={percent}
            x1="60" 
            y1={((height - 120) * (100 - percent)) / 100 + 30}
            x2={chartWidth - 30} 
            y2={((height - 120) * (100 - percent)) / 100 + 30}
            stroke="rgba(255,255,255,0.1)" 
            strokeDasharray="3,3"
          />
        ))}
        
        {/* Render all datasets - reference curves first (behind), then primary */}
        {datasets.slice().reverse().map((dataset, datasetIndex) => {
          const { data: lineData, line: lineColor, glow: glowColor, isPrimary } = dataset;
          
          return (
            <g key={datasetIndex}>
              {/* Data line - using indexValue (0-100) */}
              <polyline
                fill="none"
                stroke={lineColor}
                strokeWidth={isPrimary ? "3" : "2"}
                filter={isPrimary ? "url(#glow)" : "url(#glowLight)"}
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity={isPrimary ? 1 : 0.7}
                strokeDasharray={isPrimary ? "none" : "8,4"}
                points={lineData.map((item, index) => {
                  const x = 60 + (index * (chartWidth - 90)) / Math.max(lineData.length - 1, 1);
                  const y = ((height - 120) * (maxIndex - (item.indexValue || 0))) / maxIndex + 30;
                  return `${x},${y}`;
                }).join(' ')}
              />
              
              {/* Data points - only for primary dataset */}
              {isPrimary && lineData.map((item, index) => {
                const x = 60 + (index * (chartWidth - 90)) / Math.max(lineData.length - 1, 1);
                const y = ((height - 120) * (maxIndex - (item.indexValue || 0))) / maxIndex + 30;
                const hasData = item.count > 0;
                
                return (
                  <g key={`point-${index}`}>
                    {hasData && (
                      <circle
                        cx={x}
                        cy={y}
                        r="10"
                        fill={glowColor}
                        opacity="0.5"
                      />
                    )}
                    <circle
                      cx={x}
                      cy={y}
                      r={hasData ? "5" : "3"}
                      fill={hasData ? lineColor : "rgba(255,255,255,0.3)"}
                      stroke={hasData ? "#1c1c1e" : "transparent"}
                      strokeWidth="2"
                    />
                  </g>
                );
              })}
            </g>
          );
        })}
        
        {/* Week labels - use primary data */}
        {data.map((item, index) => {
          const x = 60 + (index * (chartWidth - 90)) / Math.max(data.length - 1, 1);
          const shouldShowLabel = data.length <= 5 ? true :
                                 data.length <= 10 ? index % 2 === 0 :
                                 data.length <= 15 ? index % 3 === 0 :
                                 index % 4 === 0;
          
          if (!shouldShowLabel) return null;
          
          return (
            <text
              key={`label-${index}`}
              x={x}
              y={height - 35}
              textAnchor="middle"
              fontSize="11"
              fill={item.count > 0 ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.4)"}
              fontWeight={item.count > 0 ? "600" : "400"}
              fontFamily="-apple-system, BlinkMacSystemFont, 'SF Pro Text', sans-serif"
            >
              {t('trendAnalysis.chart.weekLabel', { week: item.week })}
            </text>
          );
        })}
        
        {/* Y-axis labels - INDEX 0-100 */}
        {[0, 25, 50, 75, 100].map((value, idx) => (
          <text
            key={`y-${idx}`}
            x="50"
            y={((height - 120) * (maxIndex - value)) / maxIndex + 35}
            textAnchor="end"
            fontSize="12"
            fill="rgba(255,255,255,0.6)"
            fontWeight="500"
            fontFamily="-apple-system, BlinkMacSystemFont, 'SF Pro Text', sans-serif"
          >
            {value}
          </text>
        ))}
        
        {/* Y-axis label "INDEX" */}
        <text
          x="15"
          y={(height - 60) / 2}
          textAnchor="middle"
          fontSize="10"
          fill="rgba(255,255,255,0.4)"
          fontFamily="-apple-system, BlinkMacSystemFont, 'SF Pro Text', sans-serif"
          transform={`rotate(-90, 15, ${(height - 60) / 2})`}
        >
          INDEX
        </text>
        
        {/* Axis lines */}
        <line x1="60" y1="30" x2="60" y2={height - 60} stroke="rgba(255,255,255,0.2)" strokeWidth="1" />
        <line x1="60" y1={height - 60} x2={chartWidth - 30} y2={height - 60} stroke="rgba(255,255,255,0.2)" strokeWidth="1" />
      </svg>
      
      {/* Multi-line legend with max values */}
      <div className="chart-legend-multi">
        {datasets.map((dataset, idx) => (
          <div key={idx} className={`legend-item ${dataset.isPrimary ? 'primary' : 'reference'}`}>
            <span 
              className="legend-line" 
              style={{ 
                backgroundColor: dataset.line,
                boxShadow: dataset.isPrimary ? `0 0 8px ${dataset.glow}` : 'none',
                borderColor: dataset.line
              }}
            ></span>
            <span className="legend-text">
              {dataset.label}
              <span className="legend-max">{t('trendAnalysis.chart.legendMax', { value: dataset.maxValue })}</span>
            </span>
          </div>
        ))}
      </div>
      
      {/* Index explanation */}
      <div className="index-explanation">
        {t('trendAnalysis.chart.indexExplanation')}
      </div>
    </div>
  );
};

/**
 * TrendAnalysis - ✅ APPLE DARK THEME
 */
const TrendAnalysis = React.memo(() => {
  const { t } = useTranslation();

  // ✅ Symptom translation helper
  const tSymptom = (name) => t(`symptoms.${name}`, name);

  // Använd centraliserad symptomlista
  const availableSymptoms = ALL_SYMPTOMS;
  
  // State
  const [analysisMode, setAnalysisMode] = useState('combination');
  const [localSelectedSymptoms, setLocalSelectedSymptoms] = useState([]);
  const [selectedQuarter, setSelectedQuarter] = useState('Q4');
  const [trendData, setTrendData] = useState([]);
  const [cityTrendData, setCityTrendData] = useState([]);      // Reference: city level
  const [countryTrendData, setCountryTrendData] = useState([]); // Reference: country level
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastAnalysis, setLastAnalysis] = useState(null);
  const [geographicScope, setGeographicScope] = useState('postal');
  const [postalCodeFilter, setPostalCodeFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [detectedCity, setDetectedCity] = useState(''); // City detected from postal code

  // Quarter to weeks mapping
  const quarterWeeks = useMemo(() => ({
    Q1: Array.from({length: 13}, (_, i) => i + 1),
    Q2: Array.from({length: 13}, (_, i) => i + 14),
    Q3: Array.from({length: 13}, (_, i) => i + 27),
    Q4: Array.from({length: 14}, (_, i) => i + 40),
    'Available': [51, 52, 53],
    'Extended': Array.from({length: 20}, (_, i) => i + 34)
  }), []);

  // Handle symptom toggle
  const handleSymptomToggle = useCallback((symptom) => {
    const symptomName = typeof symptom === 'string' ? symptom : symptom.name || symptom._id;
    
    setLocalSelectedSymptoms(prev => {
      const newSymptoms = prev.includes(symptomName)
        ? prev.filter(s => s !== symptomName)
        : [...prev, symptomName];
      return newSymptoms;
    });
    
    setTrendData([]);
    setLastAnalysis(null);
    setError(null);
  }, []);

  // Execute trend analysis with reference curves
  const executeTrendAnalysis = useCallback(async () => {
    if (!localSelectedSymptoms || localSelectedSymptoms.length === 0) {
      setError(t('trendAnalysis.noSymptomsError'));
      return;
    }

    setTrendData([]);
    setCityTrendData([]);
    setCountryTrendData([]);
    setError(null);
    setLoading(true);

    try {
      const weeks = quarterWeeks[selectedQuarter];
      const symptomsParam = localSelectedSymptoms.join(',');
      const baseUrl = `${API_CONFIG.baseUrl}/health-data/trend?startWeek=${weeks[0]}&endWeek=${weeks[weeks.length-1]}&symptoms=${encodeURIComponent(symptomsParam)}`;
      
      // Build API calls based on geographic scope
      const apiCalls = [];
      let primaryCity = '';
      
      // Always fetch country data as reference
      apiCalls.push(
        fetch(`${baseUrl}&scope=country`)
          .then(res => res.json())
          .then(data => ({ level: 'country', data }))
      );
      
      if (geographicScope === 'postal') {
        const postalValue = postalCodeFilter.trim() || '21140';
        // Detect city from postal code (simple mapping)
        primaryCity = detectCityFromPostal(postalValue);
        setDetectedCity(primaryCity);
        
        // Fetch postal data (primary)
        apiCalls.push(
          fetch(`${baseUrl}&scope=postal&postalCode=${encodeURIComponent(postalValue)}`)
            .then(res => res.json())
            .then(data => ({ level: 'postal', data }))
        );
        
        // Fetch city data (reference)
        if (primaryCity) {
          apiCalls.push(
            fetch(`${baseUrl}&scope=city&city=${encodeURIComponent(primaryCity)}`)
              .then(res => res.json())
              .then(data => ({ level: 'city', data }))
          );
        }
      } else if (geographicScope === 'city') {
        const cityValue = cityFilter.trim() || 'Stockholm';
        
        // Fetch city data (primary)
        apiCalls.push(
          fetch(`${baseUrl}&scope=city&city=${encodeURIComponent(cityValue)}`)
            .then(res => res.json())
            .then(data => ({ level: 'city', data }))
        );
      }
      // For country scope, we only have the country call already added

      const startTime = Date.now();
      const results = await Promise.all(apiCalls);
      const executionTime = Date.now() - startTime;

      // Process results
      const processResult = (result, level) => {
        if (!result || !result.data.success) return [];
        return weeks.map(week => {
          const weekData = result.data.data.find(d => d.week === week);
          const count = weekData ? weekData.matchingReports : 0;
          return {
            week,
            count: count,
            hasData: count > 0
          };
        });
      };

      const countryResult = results.find(r => r.level === 'country');
      const cityResult = results.find(r => r.level === 'city');
      const postalResult = results.find(r => r.level === 'postal');

      // Set data based on scope
      const countryChartData = processResult(countryResult, 'country');
      setCountryTrendData(countryChartData);

      if (geographicScope === 'postal') {
        const postalChartData = processResult(postalResult, 'postal');
        const cityChartData = processResult(cityResult, 'city');
        setTrendData(postalChartData);
        setCityTrendData(cityChartData);
      } else if (geographicScope === 'city') {
        const cityChartData = processResult(cityResult, 'city');
        setTrendData(cityChartData);
        setCityTrendData([]);
      } else {
        setTrendData(countryChartData);
        setCityTrendData([]);
        setCountryTrendData([]);
      }
      
      // Calculate totals for metadata
      const primaryData = geographicScope === 'postal' ? processResult(postalResult, 'postal') :
                         geographicScope === 'city' ? processResult(cityResult, 'city') :
                         countryChartData;

      setLastAnalysis({
        mode: analysisMode,
        quarter: selectedQuarter,
        symptoms: [...localSelectedSymptoms],
        geographicScope,
        geographicFilter: geographicScope === 'postal' ? (postalCodeFilter || '21140') : 
                         geographicScope === 'city' ? (cityFilter || 'Stockholm') : t('trendAnalysis.allSweden'),
        cityReference: primaryCity || cityFilter || '',
        totalReports: primaryData.reduce((sum, item) => sum + item.count, 0),
        weeksWithData: primaryData.filter(item => item.hasData).length,
        executionTime,
        timestamp: new Date().toISOString(),
        apiCalls: apiCalls.length
      });

    } catch (error) {
      console.error('❌ Error in trend analysis:', error);
      setError(t('trendAnalysis.errorTrend', { message: error.message }));
    } finally {
      setLoading(false);
    }
  }, [localSelectedSymptoms, analysisMode, selectedQuarter, quarterWeeks, geographicScope, postalCodeFilter, cityFilter, t]);

  // Simple postal code to city mapping
  const detectCityFromPostal = (postalCode) => {
    const prefix = postalCode.toString().substring(0, 2);
    const cityMap = {
      '10': 'Stockholm', '11': 'Stockholm', '12': 'Stockholm', '13': 'Stockholm',
      '14': 'Stockholm', '15': 'Stockholm', '16': 'Stockholm', '17': 'Stockholm', '18': 'Stockholm',
      '20': 'Malmö', '21': 'Malmö', '22': 'Lund', '23': 'Trelleborg',
      '25': 'Helsingborg', '26': 'Landskrona',
      '30': 'Halmstad', '31': 'Halmstad',
      '40': 'Göteborg', '41': 'Göteborg', '42': 'Göteborg', '43': 'Göteborg', '44': 'Göteborg',
      '50': 'Borås', '51': 'Borås',
      '55': 'Jönköping', '56': 'Jönköping',
      '58': 'Linköping', '59': 'Linköping',
      '60': 'Norrköping', '61': 'Norrköping',
      '65': 'Karlstad',
      '70': 'Örebro', '71': 'Örebro',
      '72': 'Västerås', '73': 'Västerås',
      '75': 'Uppsala', '76': 'Uppsala',
      '80': 'Gävle', '81': 'Gävle',
      '85': 'Sundsvall', '86': 'Sundsvall',
      '90': 'Umeå', '91': 'Umeå',
      '95': 'Luleå', '96': 'Luleå', '97': 'Luleå'
    };
    return cityMap[prefix] || 'Stockholm';
  };

  // Handle mode change
  const handleModeChange = useCallback((mode) => {
    setAnalysisMode(mode);
    setTrendData([]);
    setCityTrendData([]);
    setCountryTrendData([]);
    setLastAnalysis(null);
    setError(null);
  }, []);

  // Handle quarter change
  const handleQuarterChange = useCallback((quarter) => {
    setSelectedQuarter(quarter);
    setTrendData([]);
    setCityTrendData([]);
    setCountryTrendData([]);
    setLastAnalysis(null);
  }, []);

  // Chart data formatting
  const chartDataFormatted = useMemo(() => {
    return trendData.map(item => ({
      ...item,
      weekLabel: `V${item.week}`,
      displayCount: item.count || 0
    }));
  }, [trendData]);

  // Get symptom display text
  const getSymptomDisplayText = useCallback(() => {
    if (!localSelectedSymptoms || localSelectedSymptoms.length === 0) return t('trendAnalysis.noSymptomsDisplay');
    if (analysisMode === 'single' && localSelectedSymptoms.length === 1) {
      return tSymptom(localSelectedSymptoms[0]);
    }
    if (analysisMode === 'combination') {
      return localSelectedSymptoms.map(s => tSymptom(s)).join(' + ');
    }
    return localSelectedSymptoms.slice(0, 2).map(s => tSymptom(s)).join(', ') + (localSelectedSymptoms.length > 2 ? ` +${localSelectedSymptoms.length - 2}` : '');
  }, [localSelectedSymptoms, analysisMode, t]);

  // Get geographic display text
  const getGeographicText = useCallback(() => {
    if (geographicScope === 'country') return t('trendAnalysis.geoCountry');
    if (geographicScope === 'city') return t('trendAnalysis.geoCity', { city: cityFilter.trim() || 'Stockholm' });
    return t('trendAnalysis.geoPostal', { code: postalCodeFilter.trim() || '21140' });
  }, [geographicScope, cityFilter, postalCodeFilter, t]);

  // Get period display text
  const getPeriodText = useCallback(() => {
    const weeks = quarterWeeks[selectedQuarter];
    if (selectedQuarter === 'Available') return 'W51-53';
    if (selectedQuarter === 'Extended') return 'W34-53';
    if (selectedQuarter === 'Q4') return 'W40-53';
    return `W${weeks[0]}-${weeks[weeks.length-1]}`;
  }, [selectedQuarter, quarterWeeks]);

  return (
    <div className="trend-analysis-dark">
      {/* Header */}
      <div className="trend-header">
        <h2>{t('trendAnalysis.title')}</h2>
        <p>{t('trendAnalysis.subtitle')}</p>
      </div>

      {/* Analysis Mode Selection */}
      <div className="trend-section">
        <h3 className="section-title">{t('trendAnalysis.analysisType')}</h3>
        <div className="mode-buttons">
          <button
            className={`mode-btn ${analysisMode === 'combination' ? 'active combination' : ''}`}
            onClick={() => handleModeChange('combination')}
          >
            <span className="mode-icon">🔬</span>
            <span className="mode-label">{t('trendAnalysis.modeCombination')}</span>
          </button>
          
          <button
            className={`mode-btn ${analysisMode === 'single' ? 'active single' : ''}`}
            onClick={() => handleModeChange('single')}
          >
            <span className="mode-icon">📊</span>
            <span className="mode-label">{t('trendAnalysis.modeSingle')}</span>
          </button>
        </div>

        <div className={`mode-explanation ${analysisMode}`}>
          {analysisMode === 'single' ? (
            <><strong>{t('trendAnalysis.singleExplainLabel')}</strong> {t('trendAnalysis.singleExplain')}</>
          ) : (
            <><strong>{t('trendAnalysis.combinationExplainLabel')}</strong> {t('trendAnalysis.combinationExplain')}</>
          )}
        </div>
      </div>

      {/* Symptom Selection */}
      <div className="trend-section">
        <h3 className="section-title">
          {t('trendAnalysis.selectSymptoms')}
          <span className="symptom-count-badge">{t('trendAnalysis.selectedCount', { count: localSelectedSymptoms.length })}</span>
        </h3>
        
        {analysisMode === 'single' && localSelectedSymptoms.length > 1 && (
          <div className="mode-warning">
            <span>{t('trendAnalysis.modeWarning', { count: localSelectedSymptoms.length })}</span>
            <button 
              className="switch-mode-btn"
              onClick={() => setAnalysisMode('combination')}
            >
              {t('trendAnalysis.switchToCombination')}
            </button>
          </div>
        )}

        <div className="symptom-chips">
          {availableSymptoms.map((symptom, index) => {
            const isSelected = localSelectedSymptoms.includes(symptom);
            return (
              <button
                key={`${symptom}-${index}`}
                className={`symptom-chip ${isSelected ? 'selected' : ''}`}
                onClick={() => handleSymptomToggle(symptom)}
              >
                {tSymptom(symptom)}
              </button>
            );
          })}
        </div>
        
        {analysisMode === 'combination' && localSelectedSymptoms.length >= 2 && (
          <div className="exact-match-info" style={{
            marginTop: '12px',
            padding: '10px 14px',
            backgroundColor: 'rgba(100, 210, 255, 0.1)',
            borderRadius: '8px',
            borderLeft: '3px solid #64d2ff',
            fontSize: '13px',
            color: '#94a3b8'
          }}>
            {t('trendAnalysis.exactMatchInfo')}
          </div>
        )}
        
        {localSelectedSymptoms.length === 0 && (
          <div className="quick-start">
            <p>{t('trendAnalysis.noSymptomsSelected')}</p>
            <div className="quick-buttons">
              <button onClick={() => setLocalSelectedSymptoms(['Feber'])}>
                {t('trendAnalysis.selectFever')}
              </button>
              <button onClick={() => setLocalSelectedSymptoms(['Feber', 'Hosta'])}>
                {t('trendAnalysis.selectFeverCough')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Period & Geographic Selection */}
      <div className="trend-section">
        <h3 className="section-title">{t('trendAnalysis.periodAndScope')}</h3>
        
        <div className="filter-row">
          <div className="filter-group">
            <label>{t('trendAnalysis.period')}</label>
            <select
              value={selectedQuarter}
              onChange={(e) => handleQuarterChange(e.target.value)}
              className="dark-select"
            >
              <option value="Extended">{t('trendAnalysis.periodExtended')}</option>
              <option value="Q4">{t('trendAnalysis.periodQ4')}</option>
              <option value="Available">{t('trendAnalysis.periodAvailable')}</option>
              <option value="Q1">{t('trendAnalysis.periodQ1')}</option>
              <option value="Q2">{t('trendAnalysis.periodQ2')}</option>
              <option value="Q3">{t('trendAnalysis.periodQ3')}</option>
            </select>
          </div>

          <div className="filter-group">
            <label>{t('trendAnalysis.scope')}</label>
            <select
              value={geographicScope}
              onChange={(e) => setGeographicScope(e.target.value)}
              className="dark-select"
            >
              <option value="postal">{t('trendAnalysis.scopePostal')}</option>
              <option value="city">{t('trendAnalysis.scopeCity')}</option>
              <option value="country">{t('trendAnalysis.scopeCountry')}</option>
            </select>
          </div>

          {geographicScope === 'postal' && (
            <div className="filter-group">
              <label>{t('trendAnalysis.postalCode')}</label>
              <input
                type="text"
                value={postalCodeFilter}
                onChange={(e) => setPostalCodeFilter(e.target.value)}
                placeholder="21140"
                className="dark-input"
              />
            </div>
          )}

          {geographicScope === 'city' && (
            <div className="filter-group">
              <label>{t('trendAnalysis.city')}</label>
              <input
                type="text"
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
                placeholder="Stockholm"
                className="dark-input"
              />
            </div>
          )}
        </div>

        {/* Execute Button */}
        <button
          onClick={executeTrendAnalysis}
          disabled={localSelectedSymptoms.length === 0 || loading}
          className={`execute-btn ${loading ? 'loading' : ''}`}
        >
          {loading ? (
            <>
              <span className="spinner"></span>
              {t('trendAnalysis.analyzing')}
            </>
          ) : (
            <>{t('trendAnalysis.runAnalysis')}</>
          )}
        </button>

        {/* Current Selection Summary */}
        <div className="selection-summary">
          <span className="summary-item">
            <strong>{t('trendAnalysis.summaryAnalysis')}</strong> {getSymptomDisplayText()}
          </span>
          <span className="summary-divider">•</span>
          <span className="summary-item">
            <strong>{t('trendAnalysis.summaryPeriod')}</strong> {getPeriodText()}
          </span>
          <span className="summary-divider">•</span>
          <span className="summary-item">
            <strong>{t('trendAnalysis.summaryScope')}</strong> {getGeographicText()}
          </span>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="trend-error">
          <span className="error-icon">❌</span>
          <span>{error}</span>
        </div>
      )}

      {/* Chart Display */}
      {trendData.length > 0 && (
        <div className="trend-section results">
          <h3 className="section-title">
            {t('trendAnalysis.resultsTitle')}: {getSymptomDisplayText()}
            <span className="period-badge">{getPeriodText()}</span>
          </h3>
          
          {/* Analysis Metadata */}
          {lastAnalysis && (
            <div className="analysis-meta">
              <span className="meta-item">
                <strong>{lastAnalysis.totalReports}</strong> {t('trendAnalysis.reports')}
              </span>
              <span className="meta-item">
                <strong>{lastAnalysis.weeksWithData}</strong> {t('trendAnalysis.weeksWithData')}
              </span>
              <span className="meta-item">
                <strong className={`primary-location ${geographicScope}`}>{lastAnalysis.geographicFilter}</strong>
              </span>
              {geographicScope === 'postal' && detectedCity && (
                <span className="meta-item reference-info">
                  {t('trendAnalysis.plusReference')} <strong className="ref-city">{detectedCity}</strong> & <strong className="ref-country">{t('trendAnalysis.chart.countryLabel')}</strong>
                </span>
              )}
              {geographicScope === 'city' && (
                <span className="meta-item reference-info">
                  {t('trendAnalysis.plusReference')} <strong className="ref-country">{t('trendAnalysis.chart.countryLabel')}</strong>
                </span>
              )}
              <span className="meta-item">
                <strong>{lastAnalysis.executionTime}ms</strong> ({lastAnalysis.apiCalls} {t('trendAnalysis.apiCalls')})
              </span>
            </div>
          )}

          {/* Chart */}
          <SimpleLineChart 
            data={chartDataFormatted} 
            cityData={cityTrendData}
            countryData={countryTrendData}
            height={400}
            analysisMode={analysisMode}
            geographicScope={geographicScope}
          />

          {/* Data Table */}
          <div className="data-table-container">
            <h4>{t('trendAnalysis.detailedWeekData')}</h4>
            <table className="trend-table">
              <thead>
                <tr>
                  <th>{t('trendAnalysis.thWeek')}</th>
                  <th>{t('trendAnalysis.thReports')}</th>
                  <th>{t('trendAnalysis.thStatus')}</th>
                </tr>
              </thead>
              <tbody>
                {trendData.map((item, index) => (
                  <tr key={item.week} className={item.hasData ? 'has-data' : ''}>
                    <td>{t('trendAnalysis.weekNum', { week: item.week })}</td>
                    <td className={item.count > 0 ? 'count-highlight' : ''}>
                      {item.count}
                    </td>
                    <td>
                      {item.hasData ? (
                        <span className="status-badge success">{t('trendAnalysis.statusData')}</span>
                      ) : (
                        <span className="status-badge empty">{t('trendAnalysis.statusNoData')}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Instructions */}
      {trendData.length === 0 && !loading && !error && (
        <div className="trend-instructions">
          <div className="instruction-icon">📈</div>
          <h3>{t('trendAnalysis.instructionsTitle')}</h3>
          <p>{t('trendAnalysis.instructionsText')}</p>
          <div className="instruction-details">
            <span>{t('trendAnalysis.instructionOptimized')}</span>
            <span>{t('trendAnalysis.instructionDataAvailable')}</span>
            <span>{t('trendAnalysis.instructionGeoFiltering')}</span>
          </div>
        </div>
      )}
    </div>
  );
});

export default TrendAnalysis;
